import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { setOnboardingComplete, setDayMode } from '../utils/localStorage';
import { ChevronRight, Heart, Moon, Star, Crown, CreditCard, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const testimonials = [
  {
    text: "This made me feel calmer instantly.",
    stars: 5,
  },
  {
    text: "I finally stopped thinking about everything.",
    stars: 5,
  },
  {
    text: "I didn't feel behind at the end of the day.",
    stars: 5,
  },
];

const screens = [
  {
    id: 1,
    heading: "This isn't about doing more.",
    subheading: "It's about deciding what's enough.",
    icon: Heart,
    buttonText: "Continue",
    bgClass: "bg-paper",
    decoration: "🌿",
  },
  {
    id: 2,
    heading: "Some days are just heavier.",
    subheading: "The Calm Keeper Compass works even then.",
    icon: Moon,
    buttonText: "That's me",
    bgClass: "bg-paper-aged",
    decoration: "🍂",
  },
  {
    id: 3,
    heading: "Unlock lifetime access",
    subheading: "One payment. Peace of mind forever.",
    icon: Crown,
    buttonText: null,
    bgClass: "bg-paper-aged",
    showSubscription: true,
    decoration: "✨",
    isFinalScreen: true,
  },
];

const OnboardingScreen = ({ screen, onNext, onSelectMode, selectedMode, onPayment, isProcessingPayment, showGiftInput, setShowGiftInput, giftCode, setGiftCode, onGiftRedeem }) => {
  const Icon = screen.icon;
  
  // Render star rating
  const renderStars = (count) => {
    return Array(count).fill(0).map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
    ));
  };
  
  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-6 md:px-12 ${screen.bgClass} paper-texture relative`}>
      <div className="max-w-md w-full text-center animate-fade-in-up relative z-10">
        {/* Icon */}
        <div className="mb-8 flex justify-center">
          <div className="w-16 h-16 rounded-full bg-paper-light shadow-lifted flex items-center justify-center border border-stone-200">
            <Icon className="w-8 h-8 text-brown" />
          </div>
        </div>
        
        {/* Heading */}
        <h1 className="font-serif text-3xl md:text-4xl text-ink-900 mb-4 leading-tight">
          {screen.heading}
        </h1>
        
        {/* Subheading */}
        <p className="font-accent text-lg md:text-xl text-ink-700 mb-8 leading-relaxed italic">
          {screen.subheading}
        </p>
        
        {/* Testimonials Section */}
        {screen.showTestimonials && (
          <div className="space-y-4 mb-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="p-4 bg-paper-light shadow-lifted border border-stone-200 text-left">
                <div className="flex gap-1 mb-2">
                  {renderStars(testimonial.stars)}
                </div>
                <p className="font-accent text-ink-700 italic">&ldquo;{testimonial.text}&rdquo;</p>
              </Card>
            ))}
          </div>
        )}
        
        {/* Subscription Section */}
        {screen.showSubscription && (
          <div className="mb-8">
            <Card className="p-6 bg-paper-light shadow-lifted-lg border-2 border-brown/30">
              <div className="mb-4">
                <span className="text-5xl font-serif text-brown font-bold">$15</span>
                <span className="text-ink-500 font-accent ml-2">one-time</span>
              </div>
              <h3 className="font-serif text-xl text-ink-900 mb-2">Lifetime Access</h3>
              <ul className="text-left space-y-2 mb-6">
                <li className="flex items-center gap-2 text-ink-700 font-accent">
                  <span className="text-sage">✓</span> All features unlocked forever
                </li>
                <li className="flex items-center gap-2 text-ink-700 font-accent">
                  <span className="text-sage">✓</span> AI meal &amp; craft ideas
                </li>
                <li className="flex items-center gap-2 text-ink-700 font-accent">
                  <span className="text-sage">✓</span> Future updates included
                </li>
                <li className="flex items-center gap-2 text-ink-700 font-accent">
                  <span className="text-sage">✓</span> No subscriptions, ever
                </li>
              </ul>
              
              {/* Payment methods indicator */}
              <div className="flex items-center justify-center gap-2 mb-4 text-ink-500 text-sm">
                <CreditCard className="w-4 h-4" />
                <span className="font-accent">Credit Card • Google Pay • Apple Pay</span>
              </div>
              
              <Button
                data-testid="subscription-btn"
                onClick={onPayment}
                disabled={isProcessingPayment}
                className="w-full vintage-btn btn-brown rounded-lg py-6 text-lg"
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Get Lifetime Access
                  </>
                )}
              </Button>
              
              {/* Gift Code Section */}
              <div className="mt-4 pt-4 border-t border-stone-200">
                {!showGiftInput ? (
                  <button
                    data-testid="gift-code-toggle"
                    onClick={() => setShowGiftInput(true)}
                    className="text-sm text-ink-500 hover:text-brown font-accent italic flex items-center justify-center gap-1 mx-auto transition-colors"
                  >
                    <Gift className="w-3.5 h-3.5" />
                    Have a gift code?
                  </button>
                ) : (
                  <div className="space-y-2 animate-fade-in">
                    <input
                      data-testid="gift-code-input"
                      type="password"
                      value={giftCode}
                      onChange={(e) => setGiftCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && onGiftRedeem()}
                      placeholder="Enter gift code"
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-300 bg-white text-center font-accent text-ink-700 focus:outline-none focus:border-brown focus:ring-1 focus:ring-brown/30"
                    />
                    <Button
                      data-testid="gift-code-redeem-btn"
                      onClick={onGiftRedeem}
                      disabled={!giftCode.trim()}
                      className="w-full vintage-btn bg-sage hover:bg-sage/90 text-white rounded-lg py-2"
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      Redeem
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
        
        {/* Day Mode Selection for screen 3 */}
        {screen.showDayModes && (
          <div className="space-y-4 mb-8">
            <button
              data-testid="onboarding-mode-survival"
              onClick={() => onSelectMode('survival')}
              className={`w-full p-4 rounded-lg border-2 transition-all recipe-card ${
                selectedMode === 'survival' 
                  ? 'bg-survival-bg border-survival-border shadow-lifted-lg' 
                  : 'bg-paper-light border-stone-200 hover:border-survival-border'
              }`}
            >
              <span className="flex items-center justify-center gap-3">
                <span className="text-2xl">🔴</span>
                <span className={`font-serif font-medium ${selectedMode === 'survival' ? 'text-survival-text' : 'text-ink-900'}`}>
                  Survival Day
                </span>
              </span>
            </button>
            
            <button
              data-testid="onboarding-mode-normal"
              onClick={() => onSelectMode('normal')}
              className={`w-full p-4 rounded-lg border-2 transition-all recipe-card ${
                selectedMode === 'normal' 
                  ? 'bg-normal-bg border-normal-border shadow-lifted-lg' 
                  : 'bg-paper-light border-stone-200 hover:border-normal-border'
              }`}
            >
              <span className="flex items-center justify-center gap-3">
                <span className="text-2xl">🟡</span>
                <span className={`font-serif font-medium ${selectedMode === 'normal' ? 'text-normal-text' : 'text-ink-900'}`}>
                  Normal Day
                </span>
              </span>
            </button>
            
            <button
              data-testid="onboarding-mode-good"
              onClick={() => onSelectMode('good')}
              className={`w-full p-4 rounded-lg border-2 transition-all recipe-card ${
                selectedMode === 'good' 
                  ? 'bg-good-bg border-good-border shadow-lifted-lg' 
                  : 'bg-paper-light border-stone-200 hover:border-good-border'
              }`}
            >
              <span className="flex items-center justify-center gap-3">
                <span className="text-2xl">🟢</span>
                <span className={`font-serif font-medium ${selectedMode === 'good' ? 'text-good-text' : 'text-ink-900'}`}>
                  Good Day
                </span>
              </span>
            </button>
          </div>
        )}
        
        {/* Button */}
        {screen.buttonText && (
          <Button
            data-testid={`onboarding-btn-${screen.id}`}
            onClick={onNext}
            className="vintage-btn btn-brown rounded-lg px-8 py-6 text-lg"
          >
            {screen.buttonText}
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        )}
        
        {screen.showDayModes && selectedMode && (
          <Button
            data-testid="onboarding-continue-with-mode"
            onClick={onNext}
            className="vintage-btn btn-brown rounded-lg px-8 py-6 text-lg mt-4"
          >
            Continue
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        )}
      </div>
      
      {/* Progress dots */}
      <div className="absolute bottom-8 flex gap-2">
        {screens.map((_, idx) => (
          <div
            key={idx}
            className={`h-2 rounded-full transition-all ${
              idx === screens.findIndex(s => s.id === screen.id)
                ? 'bg-brown w-6'
                : 'bg-stone-300 w-2'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [selectedMode, setSelectedMode] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showGiftInput, setShowGiftInput] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  
  const handleNext = () => {
    if (currentScreen < screens.length - 1) {
      setCurrentScreen(currentScreen + 1);
    } else {
      setOnboardingComplete();
      if (selectedMode) {
        setDayMode(selectedMode);
      }
      navigate('/dashboard');
    }
  };
  
  const handleSelectMode = (mode) => {
    setSelectedMode(mode);
  };
  
  const handlePayment = async () => {
    setIsProcessingPayment(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        {
          package_id: 'lifetime',
          origin_url: originUrl
        }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handleGiftRedeem = () => {
    if (giftCode.trim().toLowerCase() === 'familyfriends') {
      localStorage.setItem('calm_keeper_lifetime_access', 'true');
      toast.success('Welcome! Lifetime access granted.');
      setOnboardingComplete();
      navigate('/dashboard');
    } else {
      toast.error('Invalid gift code. Please try again.');
      setGiftCode('');
    }
  };
  
  return (
    <OnboardingScreen
      screen={screens[currentScreen]}
      onNext={handleNext}
      onSelectMode={handleSelectMode}
      selectedMode={selectedMode}
      onPayment={handlePayment}
      isProcessingPayment={isProcessingPayment}
      showGiftInput={showGiftInput}
      setShowGiftInput={setShowGiftInput}
      giftCode={giftCode}
      setGiftCode={setGiftCode}
      onGiftRedeem={handleGiftRedeem}
    />
  );
};

export default Onboarding;
